import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Users, Sparkles, ArrowRight, Filter, X } from 'lucide-react';
import { playSoundIfEnabled } from '../utils/sounds';
import { workshops } from '../mock/mockData';

const Workshops = () => {
  const [filter, setFilter] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  const [filteredWorkshops, setFilteredWorkshops] = useState(workshops);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Using static workshop data (live availability disabled)
  useEffect(() => {
    let filtered = workshops;
    
    if (filter !== 'all') {
      filtered = filtered.filter(w => w.season.toLowerCase() === filter.toLowerCase());
    }
    
    if (ageFilter !== 'all') {
      filtered = filtered.filter(w => w.ageGroup === ageFilter);
    }
    
    setFilteredWorkshops(filtered);
  }, [filter, ageFilter]); // Added all dependencies

  const getThemeForSeason = (season) => {
    const themes = {
      'Summer': 'summer',
      'Christmas': 'christmas',
      'Halloween': 'halloween',
      'Easter': 'easter'
    };
    return themes[season] || 'general';
  };

  const handleFilterChange = (newFilter) => {
    const theme = getThemeForSeason(newFilter);
    playSoundIfEnabled('click', theme);
    setFilter(newFilter);
  };

  const handleAgeFilterChange = (newAgeFilter) => {
    playSoundIfEnabled('click');
    setAgeFilter(newAgeFilter);
  };

  const seasons = ['all', 'Summer', 'Easter', 'Halloween', 'Christmas'];
  const ageGroups = ['all', '7-10 years', '11-14 years'];

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-light-pink/10 rounded-full px-4 py-2 mb-4">
            <Calendar className="w-4 h-4 text-light-pink" />
            <span className="text-light-pink text-sm font-mono uppercase tracking-wider">
              All Workshops
            </span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-display font-bold text-white mb-6">
            Holiday Drama Workshops
          </h1>
          <p className="text-xl text-mid-grey max-w-3xl mx-auto">
            Discover the magic of Shakespeare through our engaging, age-appropriate drama workshops. 
            Running during all school holidays throughout the year.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Season Filter */}
            <div className="w-full lg:w-auto">
              <div className="flex items-center gap-2 mb-3">
                <Filter className="w-4 h-4 text-light-pink" />
                <span className="text-light-pink text-sm font-semibold">Filter by Season:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {seasons.map((season) => (
                  <button
                    key={season}
                    onClick={() => handleFilterChange(season)}
                    className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-wider transition-all duration-300 ${
                      filter === season
                        ? 'bg-light-pink text-black'
                        : 'bg-white/5 text-mid-grey hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {season === 'all' ? 'All Seasons' : season}
                  </button>
                ))}
              </div>
            </div>

            {/* Age Filter */}
            <div className="w-full lg:w-auto">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-mid-purple" />
                <span className="text-mid-purple text-sm font-semibold">Filter by Age:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {ageGroups.map((age) => (
                  <button
                    key={age}
                    onClick={() => handleAgeFilterChange(age)}
                    className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-wider transition-all duration-300 ${
                      ageFilter === age
                        ? 'bg-mid-purple text-white'
                        : 'bg-white/5 text-mid-grey hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {age === 'all' ? 'All Ages' : age}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {(filter !== 'all' || ageFilter !== 'all') && (
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="text-mid-grey text-sm">Active filters:</span>
              {filter !== 'all' && (
                <div className="flex items-center gap-2 bg-light-pink/20 text-light-pink px-3 py-1 rounded-full text-sm">
                  <span>{filter}</span>
                  <button onClick={() => handleFilterChange('all')}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              {ageFilter !== 'all' && (
                <div className="flex items-center gap-2 bg-mid-purple/20 text-mid-purple px-3 py-1 rounded-full text-sm">
                  <span>{ageFilter}</span>
                  <button onClick={() => handleAgeFilterChange('all')}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Workshop Grid */}
        {filteredWorkshops.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {filteredWorkshops.map((workshop, index) => (
              <div
                key={workshop.id}
                className="group relative"
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  opacity: 0,
                  animation: 'fadeInUp 0.6s ease-out forwards'
                }}
              >
                <div className={`relative rounded-3xl overflow-hidden border-2 border-transparent hover:border-light-pink/50 transition-all duration-500 hover:-translate-y-2 bg-${workshop.color}`}>
                  {/* Image Section */}
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={workshop.image}
                      alt={workshop.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    
                    {/* Status Badge */}
                    {workshop.status === 'sold-out' && (
                      <div className="absolute top-4 right-4 status-badge status-sold-out">
                        SOLD OUT
                      </div>
                    )}
                    {workshop.status === 'opening-soon' && (
                      <div className="absolute top-4 right-4 status-badge status-opening-soon">
                        BOOKING OPENING SOON
                      </div>
                    )}
                    {workshop.status !== 'sold-out' && workshop.status !== 'opening-soon' && (
                      <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold border border-white/20">
                        {workshop.spotsLeft} spots left
                      </div>
                    )}

                    {/* Season Badge */}
                    <div className={`absolute top-4 left-4 bg-${workshop.color} text-black px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wider font-semibold`}>
                      {workshop.season}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-8">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-black/20 text-black text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full">
                        {workshop.ageGroup}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold text-black mb-3 group-hover:text-black/80 transition-colors">
                      {workshop.title}
                    </h3>

                    <p className="text-black/70 mb-6 leading-relaxed">
                      {workshop.description}
                    </p>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-black/10">
                      <div className="flex items-center gap-2 text-black/80">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{workshop.dates}</span>
                      </div>
                      <div className="flex items-center gap-2 text-black/80">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{workshop.time}</span>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mb-6">
                      <h4 className="text-black font-semibold mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        What's Included:
                      </h4>
                      <ul className="space-y-2">
                        {workshop.highlights.slice(0, 3).map((highlight, idx) => (
                          <li key={idx} className="text-sm text-black/70 flex items-start gap-2">
                            <span className="text-black mt-1">•</span>
                            <span>{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Price and CTA */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-black/60 mb-1">Workshop Price</div>
                        <div className="text-3xl font-bold text-black">£{workshop.price}</div>
                      </div>
                      {workshop.status === 'sold-out' ? (
                        <div className="status-badge status-sold-out">
                          SOLD OUT
                        </div>
                      ) : workshop.status === 'opening-soon' ? (
                        <div className="status-badge status-opening-soon text-xs px-3 py-2">
                          BOOKING OPENING SOON
                        </div>
                      ) : (
                        <Link
                          to={`/booking/${workshop.id}`}
                          className="cta-button bg-black text-white hover:bg-black/80 inline-flex items-center gap-2 group/btn"
                          onClick={() => {
                            const theme = getThemeForSeason(workshop.season);
                            playSoundIfEnabled('click', theme);
                          }}
                        >
                          Book Now
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎭</div>
            <h3 className="text-2xl font-semibold text-white mb-2">No workshops found</h3>
            <p className="text-mid-grey mb-6">Try adjusting your filters to see more options</p>
            <button
              onClick={() => {
                handleFilterChange('all');
                handleAgeFilterChange('all');
              }}
              className="cta-button bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 bg-gradient-to-br from-light-pink/10 to-mid-purple/10 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-light-pink/20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-display font-bold text-white mb-4">
                Not sure which workshop to choose?
              </h3>
              <p className="text-mid-grey mb-6 leading-relaxed">
                We're here to help! Contact us to discuss which workshop would be perfect for your child's 
                age, experience level, and interests. Every child's journey into drama is unique!
              </p>
              <Link
                to="/contact"
                className="cta-button bg-light-pink text-black hover:bg-mid-yellow transition-all duration-300 inline-flex items-center gap-2"
                onClick={() => playSoundIfEnabled('click')}
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1731703809282-44225fb895a7"
                alt="Children in workshop"
                className="rounded-2xl shadow-2xl border-4 border-light-pink/20"
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Workshops;
